import { useEffect, useMemo } from 'react';
import { FloatingLabel, Form } from 'react-bootstrap';

import { buildFolderIdPathOptions } from 'helpers/folderPaths';
import { getFolders } from 'redux/slices/lists/foldersSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

interface FolderSelectProps {
  readonly value: number | null;
  readonly onChange: (folderId: number | null) => void;
  // Restrict options to one zone's folders (zone-scoped objects). Pass
  // whichever the modal holds — zoneId or zoneName. Omit both for the
  // non-zone-scoped objects (connections, python_libraries) to show all.
  readonly zoneId?: number | null;
  readonly zoneName?: string | null;
  // When omitted, renders a bare <Form.Select> so the surrounding modal can
  // supply its own <Form.Label> (the common react-bootstrap modal style).
  // Pass a label to wrap it in a FloatingLabel instead.
  readonly label?: string;
  // Text for the leading "no folder" option (maps to null). Defaults to
  // "None"; pass e.g. "Select Folder" where there's no field label for context.
  readonly noneLabel?: string;
  readonly disabled?: boolean;
}

/**
 * Folder picker that deals in `folder_id` (the wire field the nap-backend
 * #1076 top-level objects accept), shared across the redux/useState edit
 * modals. Loads the flat folder list once, renders full paths, and offers a
 * leading "None" option that maps to `null` (clears the folder server-side).
 */
export const FolderSelect = ({ value, onChange, zoneId, zoneName, label, noneLabel = 'None', disabled }: FolderSelectProps) => {
  const dispatch = useAppDispatch();
  const folders = useAppSelector((state) => state.listOfFolders.folders);

  useEffect(() => {
    dispatch(getFolders());
  }, [dispatch]);

  const options = useMemo(() => {
    const all = folders ?? [];
    // Neither zone prop supplied → not zone-scoped (connections,
    // python_libraries): show every folder.
    if (zoneId === undefined && zoneName === undefined) {
      return buildFolderIdPathOptions(all);
    }
    // Zone-scoped: restrict to the selected zone. When no zone is selected yet
    // (null id / empty name) there are no valid folders to offer.
    const scoped =
      zoneName !== undefined ? (zoneName ? all.filter((f) => f.zone_name === zoneName) : []) : zoneId != null ? all.filter((f) => f.zone_id === zoneId) : [];
    return buildFolderIdPathOptions(scoped);
  }, [folders, zoneId, zoneName]);

  const select = (
    <Form.Select
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? null : Number(v));
      }}
      disabled={disabled}
    >
      <option value="">{noneLabel}</option>
      {options.map((o) => (
        <option key={o.folder_id} value={o.folder_id}>
          {o.path}
        </option>
      ))}
    </Form.Select>
  );

  return label !== undefined ? <FloatingLabel label={label}>{select}</FloatingLabel> : select;
};
